import { Methods } from '../../api/methods.js';
import type { ParsedArgs } from '../args.js';
import { booleanFlag, flag, hasFlag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import type { CommandResult } from '../output.js';
import { parseJsonObject, pickDefined, withClient } from './common.js';

export async function profilesCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[1] ?? 'list';
  switch (action) {
    case 'list':
      return request(args, Methods.Profiles);
    case 'active':
      return request(args, Methods.ProfilesActive);
    case 'new':
      return request(args, Methods.ProfilesNew, profileParams(args, true));
    case 'update':
      return request(args, Methods.ProfilesUpdate, profileParams(args, false));
    case 'delete': {
      const profileId = args.positionals[2] ?? flag(args.flags, 'profile-id');
      if (!profileId) throw new CliError('profiles delete requires <profile-id>', ExitCode.Usage);
      return request(args, Methods.ProfilesDelete, { profileId });
    }
    case 'switch': {
      const profileId = flag(args.flags, 'profile-id');
      const switchId = flag(args.flags, 'switch-id');
      if (profileId && switchId) {
        throw new CliError('profiles switch accepts only one target ID', ExitCode.Usage);
      }
      return request(
        args,
        Methods.ProfilesSwitch,
        pickDefined({ profileId, switchId, pin: flag(args.flags, 'pin') }),
      );
    }
    case 'verify': {
      const profileId = flag(args.flags, 'profile-id');
      const switchId = flag(args.flags, 'switch-id');
      if ((!profileId && !switchId) || (profileId && switchId)) {
        throw new CliError(
          'profiles verify requires exactly one of --profile-id or --switch-id',
          ExitCode.Usage,
        );
      }
      return request(
        args,
        Methods.ProfilesVerify,
        pickDefined({ profileId, switchId, pin: flag(args.flags, 'pin') }),
      );
    }
    default:
      throw new CliError(`Unknown profiles action "${action}"`, ExitCode.Usage);
  }
}

function profileParams(args: ParsedArgs, creating: boolean): Record<string, unknown> {
  const raw = flag(args.flags, 'params');
  if (raw) return parseJsonObject(raw, '--params');
  const profileId = args.positionals[2] ?? flag(args.flags, 'profile-id');
  if (!creating && !profileId) {
    throw new CliError('profiles update requires <profile-id>', ExitCode.Usage);
  }
  const result = pickDefined({
    profileId: creating ? undefined : profileId,
    name: flag(args.flags, 'name'),
    role: flag(args.flags, 'role'),
    pin: flag(args.flags, 'pin'),
    limitsEnabled: booleanFlag(args.flags, 'limits-enabled'),
    dailyLimit: flag(args.flags, 'daily-limit'),
    sessionLimit: flag(args.flags, 'session-limit'),
    clearPin: hasFlag(args.flags, 'clear-pin') ? true : undefined,
    clearLimits: hasFlag(args.flags, 'clear-limits') ? true : undefined,
    regenerateSwitchId: hasFlag(args.flags, 'regenerate-switch-id') ? true : undefined,
  });
  if (creating && !result?.name) throw new CliError('profiles new requires --name', ExitCode.Usage);
  if (!creating && result && Object.keys(result).length === 1) {
    throw new CliError('profiles update requires a changed field', ExitCode.Usage);
  }
  return result ?? {};
}

async function request(args: ParsedArgs, method: string, params?: unknown): Promise<CommandResult> {
  return { data: await withClient(args.options, (client) => client.request(method, params)) };
}
